import _ from 'lodash';
import { isValidCPF, isValidCNPJ } from '@brazilian-utils/brazilian-utils';
import { DateTime } from 'luxon';
import { join } from './strings';
import option from './option';
import { livechatSupport } from '../constants/support';

function brl(value: number | string = 0, isFloat = false): string {
    const toFormat =
        typeof value === 'number' ? value : parseInt(value ?? 0, 10);
    return Intl.NumberFormat('pt-br', { style: 'currency', currency: 'BRL' })
        .format(toFormat / (isFloat ? 1 : 100))
        .toString();
}

function roundBrl(value = 0): string {
    return brl(Math.round(value / 100) * 100).replace(',00', '');
}

function truncBrl(value = 0): string {
    return brl(Math.trunc(value));
}

function brlNumber(value = 0): string {
    return brl(value).replace('R$ ', '');
}

function formatNumber(
    value: number | string = 0,
    minimumFractionDigits: number | undefined = undefined
): string {
    const toFormat = typeof value === 'number' ? value : parseInt(value ?? 0);
    return toFormat.toLocaleString('pt-br', {
        minimumFractionDigits: minimumFractionDigits,
    });
}

function buildWhatsappLink(message?: string, phoneNumber = ''): string {
    const encoded = encodeURIComponent(message ?? '');
    return `https://wa.me/${phoneNumber}?text=${encoded}`;
}

function buildLivechatSupportLink(_: string): string {
    return livechatSupport;
}

function cpf(value: number | string = 0): string {
    const cpfNumber =
        typeof value === 'number'
            ? value.toString().padStart(11, '0').substring(0, 11)
            : value;
    return cpfNumber
        .toString()
        .replace(
            /([0-9*]{3})([0-9*]{3})([0-9*]{3})([0-9*]{2})/g,
            '$1.$2.$3-$4'
        );
}

function cnpj(value: number | string = 0): string {
    const cpfNumber =
        typeof value === 'number'
            ? value.toString().padStart(14, '0').substring(0, 14)
            : value;
    return cpfNumber
        .toString()
        .replace(
            /([0-9*]{2})([0-9*]{3})([0-9*]{3})([0-9*]{4})([0-9*]{2})/g,
            '$1.$2.$3/$4-$5'
        );
}

function zipcode(value: string | null): string | null {
    return value ? value.replace(/(\d{5})-?(\d{3})/g, '$1-$2') : null;
}

function enumerate(values: string[]): string {
    return values.reduce((partial: string, value: string, index: number) => {
        if (index === 0) return value;
        return index === 1 ? `${value} e ${partial}` : `${value}, ${partial}`;
    }, '');
}

type RangeStyle = 'between' | 'to' | 'until' | 'fromTo';
type RangeParams<T> = {
    min?: T;
    max?: T;
    capitalize?: boolean;
    style?: RangeStyle;
    formatter?: (a: T) => string;
};

const minOnly = <T>(min?: T, max?: T) => min && max === undefined;
const maxOnly = <T>(min?: T, max?: T) => max && min === undefined;

function range<T>({
    min,
    max,
    capitalize = false,
    style = 'between',
    formatter = _.identity,
}: RangeParams<T>): string {
    const formattedMin = option.map(formatter, min);
    const formattedMax = option.map(formatter, max);
    const toStyle: Record<RangeStyle, () => string> = {
        between: () => `entre ${formattedMin} e ${formattedMax}`,
        to: () => `${formattedMin} a ${formattedMax}`,
        until: () => `${formattedMin} até ${formattedMax}`,
        fromTo: () => `de ${formattedMin} até ${formattedMax}`,
    };

    let text: string;
    if (minOnly(min, max)) {
        text = `a partir de ${formattedMin}`;
    } else if (maxOnly(min, max)) {
        text = `até ${formattedMax}`;
    } else {
        text = toStyle[style]();
    }

    return capitalize ? text.replace(/^[A-z]/g, c => c.toUpperCase()) : text;
}

function formatPhone(phone?: string | null): string {
    return (phone || '')
        .replace(/^55/, '')
        .replace(/^([0-9]{2})([0-9]{4,5})([0-9]{4})$/, '($1) $2-$3');
}

function newLinesToHTML(text: string): string {
    return text.replace(/(?:\r\n|\r|\n)/g, '<br />');
}

const normalize = (text: string): string =>
    text
        .toLowerCase()
        // Remove diacritics
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

const numberInflection = (
    count: number,
    singular: string,
    plural: string
): string => (count === 0 || Math.abs(count) === 1 ? singular : plural);

const genderInflection = (
    isMasculine: boolean,
    masculine: string,
    feminine: string
): string => (isMasculine ? masculine : feminine);

const dateInflection = (date: DateTime, past: string, future: string): string =>
    date < DateTime.now() ? past : future;

function pluralize(
    count: number,
    suffix: string,
    pluralized: string = suffix + 's'
): string {
    return `${formatNumber(count)} ${numberInflection(
        count,
        suffix,
        pluralized
    )}`;
}

function titleCase(text: string): string {
    return join(splitName(text.trim().toLowerCase()).map(capitalize));
}

function sentenceCase(text: string): string {
    return text
        .trim()
        .toLowerCase()
        .replace(/^[A-z]/g, c => c.toUpperCase());
}

function percentage(
    value: number | string = 0,
    minimum = 2,
    maximum = minimum
): string {
    const toFormat =
        typeof value === 'number' ? value : parseInt(value ?? 0) / 100;
    return (
        toFormat.toLocaleString('pt-br', {
            minimumFractionDigits: minimum,
            maximumFractionDigits: maximum,
        }) + '%'
    );
}

function monthlyInterest(value: number | string = 0, precision = 2): string {
    return `${percentage(value, precision)} (a.m.)`;
}

function discountPercentage(
    fullPrice: number,
    discountedPrice: number,
    precision = 2
): string {
    if (fullPrice === 0) return '-';
    const discount = ((fullPrice - discountedPrice) * 100) / fullPrice;
    return percentage(discount, precision);
}

function firstName(name: string): string {
    return splitName(name)[0];
}

function limit(name: string, length: number): string {
    return name.length > length ? name.slice(0, length - 3) + '...' : name;
}

function cardName({
    brand,
    lastDigits,
}: {
    brand: string;
    lastDigits: string;
}): string {
    return `${titleCase(brand)} **** ${lastDigits}`;
}

export type AddressArgs = {
    zipcode?: string | null;
    roadType?: string | null;
    street?: string | null;
    streetNumber?: string | null;
    complement?: string | null;
    neighborhood?: string | null;
    city?: string | null;
    state?: string | null;
};

function address({
    street,
    streetNumber,
    zipcode,
    roadType,
    complement,
    neighborhood,
    city,
    state,
}: AddressArgs): string {
    const streetPart = titleCase(join([roadType, street, streetNumber], ' '));
    const cityPart = titleCase(join([neighborhood, city], ' - '));
    return join([streetPart, zipcode, complement, cityPart, state], ', ');
}

function cpfCnpj(doc: string): string {
    return doc.length === 14 ? cnpj(doc) : cpf(doc);
}

function splitName(name: string): string[] {
    return name.split(' ');
}

function capitalize<T extends string | undefined>(string: T): T;
function capitalize(string: string | undefined): string | undefined {
    if (string === undefined) return undefined;
    return string.charAt(0).toUpperCase() + string.slice(1);
}

function getCapitalizedWord(string: string, index = 0): string {
    const split = splitName(string);
    const name = split.length >= index ? split[index] : '';
    return titleCase(name);
}

function removeNonNumeric(string: string, removeSign = true): string {
    return removeSign
        ? string.replace(/[^0-9]/g, '')
        : string.replace(/[^0-9-]/g, '');
}

function pix(key: string): string {
    if (key.startsWith('+')) {
        return formatPhone(key.slice(1));
    } else if (isValidCPF(key)) {
        return cpf(key);
    } else if (isValidCNPJ(key)) {
        return cnpj(key);
    }
    return key;
}

function formatHiddenCpfCnpj(document: string): string {
    const doc = removeNonNumeric(document);

    const cnpj = doc.length === 14;
    const cpf = doc.length === 11;
    if (cnpj) {
        return `${doc.slice(0, 2)}.***.***/${doc.slice(8, 12)}-${doc.slice(
            12,
            14
        )}`;
    } else if (cpf) {
        return `***.${doc.slice(3, 6)}.${doc.slice(6, 9)}-**`;
    } else {
        return doc;
    }
}

function accountNumber(account: string, isEncrypted: boolean): string {
    if (isEncrypted) {
        return '******-*';
    }
    const length = account.length;
    return `${account.slice(0, length - 1)}-${account.charAt(length - 1)}`;
}

function branch(branch: string, isEncrypted: boolean): string {
    if (isEncrypted) {
        return '****';
    }
    return branch.padStart(4, '0');
}

type BankAccountFormatParams = {
    branch: string;
    accountNumber: string;
    bankName: string;
    pixKey?: string | null;
    isEncrypted?: boolean | null;
};

function bankAccount({
    branch: _branch,
    accountNumber: _accountNumber,
    bankName,
    pixKey,
    isEncrypted = false,
}: BankAccountFormatParams): string {
    if (isEncrypted && pixKey) {
        return `Pix: ${pix(pixKey)}`;
    }
    return `${titleCase(bankName)} / ${branch(
        _branch,
        Boolean(isEncrypted)
    )} / ${accountNumber(_accountNumber, Boolean(isEncrypted))}`;
}

function documentType(document: string): 'CNPJ' | 'CPF' {
    return document.length >= 14 ? 'CNPJ' : 'CPF';
}

function phoneType(phone: string): 'landline' | 'mobile' {
    return parseInt(phone[2]) > 5 ? 'mobile' : 'landline';
}

function installments(count: number, value: number) {
    return `${count}x ${brl(value)}`;
}

export default {
    address,
    brl,
    brlNumber,
    buildWhatsappLink,
    capitalize,
    cardName,
    cnpj,
    cpf,
    cpfCnpj,
    dateInflection,
    discountPercentage,
    enumerate,
    firstName,
    formatNumber,
    formatPhone,
    getCapitalizedWord,
    monthlyInterest,
    newLinesToHTML,
    normalize,
    numberInflection,
    genderInflection,
    percentage,
    pix,
    pluralize,
    range,
    removeNonNumeric,
    roundBrl,
    sentenceCase,
    splitName,
    titleCase,
    zipcode,
    formatHiddenCpfCnpj,
    buildLivechatSupportLink,
    branch,
    accountNumber,
    documentType,
    limit,
    phoneType,
    bankAccount,
    truncBrl,
    installments,
};
